import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { IconExternalLink, IconTrophy } from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useAuthStore } from '../../../store/authStore';
import contestService from '../../../services/contestService';
import ContestHero from '../../../components/user/ContestHero';
import ContestStandingCard from '../../../components/user/ContestStandingCard';
import ContestEarnCard from '../../../components/user/ContestEarnCard';
import ContestTopList from '../../../components/user/ContestTopList';
import ContestSkeleton from '../../../components/user/ContestSkeleton';
import { CARD } from '../../../components/user/contestUi';
import { CONTEST_SITE_URL } from '../../../config/contestSite';

// Contest page (header + footer from the layout only):
//   hero (status, dates, countdown) → my standing (rank, points, goal, breakdown) →
//   LEFT = ways to earn points · RIGHT = leaderboard (podium, top 50, my row).
// Contest points are separate from credits. Owners only — operators compete under the owner.
const SHOW_PODIUM = true; // false → every position (1–50) is a plain list row

export default function ContestPage() {
    const { t } = useTranslation();
    const user = useAuthStore((s) => s.user);
    const isOperator = user?.role === 'OPERATOR';

    const [board, setBoard] = useState(null); // public /leaderboard: { phase, contest, rules, stats, leaderboard }
    const [me, setMe] = useState(null);       // /api/contest/me
    const [loading, setLoading] = useState(true);

    const loadMe = useCallback(() => contestService.getMyPoints().then(setMe).catch(() => {}), []);
    const loadBoard = useCallback(() => contestService.getLeaderboard().then(setBoard).catch(() => {}), []);

    useEffect(() => {
        if (isOperator) return;
        let alive = true;
        Promise.all([loadBoard(), loadMe()]).finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [isOperator, loadBoard, loadMe]);

    // The countdown hit zero → the phase changed on the server (live → pending): refresh both.
    const lastRefresh = useRef(0);
    const onElapsed = useCallback(() => {
        if (Date.now() - lastRefresh.current < 3000) return;
        lastRefresh.current = Date.now();
        loadBoard();
        loadMe();
    }, [loadBoard, loadMe]);

    const phase = board?.phase || 'inactive';

    // While results are being processed an admin publishes them by hand (pending → completed):
    // check once a minute so the standings appear without a manual reload.
    useEffect(() => {
        if (phase !== 'pending') return undefined;
        const id = setInterval(() => { loadBoard(); loadMe(); }, 60 * 1000);
        return () => clearInterval(id);
    }, [phase, loadBoard, loadMe]);

    if (isOperator) return <Navigate to="/" replace />;

    const running = ['live', 'pending', 'upcoming', 'completed'].includes(phase); // otherwise: no contest is running
    const winnerCount = board?.rules?.winnerCount;

    return (
        <div className="flex flex-col gap-5 py-3 font-hind sm:py-6">
            {loading ? (
                <ContestSkeleton />
            ) : !board || !running ? (
                <div className={`${CARD} flex flex-col items-center gap-3 px-6 py-16 text-center`}>
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"><IconTrophy size={28} /></span>
                    <div className="text-sm text-zinc-500 dark:text-zinc-400">{t('contest.page.noContest')}</div>
                    <a href={CONTEST_SITE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                        {t('contest.hero.details')}<IconExternalLink size={14} />
                    </a>
                </div>
            ) : (
                <>
                    <ContestHero contest={board.contest} phase={phase} winnerCount={winnerCount} onElapsed={onElapsed} />

                    {me && <ContestStandingCard me={me} phase={phase} leaderboard={board.leaderboard} winnerCount={winnerCount} />}

                    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,480px),1fr))] items-start gap-5">
                        {/* LEFT — ways to earn points */}
                        <ContestEarnCard data={me} phase={phase} onChanged={loadMe} />
                        {/* RIGHT — leaderboard */}
                        <ContestTopList
                            leaderboard={board.leaderboard}
                            me={me}
                            phase={phase}
                            winnerCount={winnerCount}
                            showPodium={SHOW_PODIUM}
                            currentUserName={user?.name}
                            currentUserAvatar={user?.profilePicture}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
